class DlPageReviewCardsY extends HTMLElement {
  constructor() {
    super();
    this.refs = null;
    this.swiper = null;
    this.initRetryTimer = null;
    this.desktopSnapPositions = [];
    this.currentDesktopSnapIndex = 0;
    this.isDesktopDragging = false;
    this.desktopDragPointerId = null;
    this.desktopDragStartX = 0;
    this.desktopDragStartTranslate = 0;
    this.desktopDragCurrentTranslate = 0;

    this.handlePrevButtonClick = this.handlePrevButtonClick.bind(this);
    this.handleNextButtonClick = this.handleNextButtonClick.bind(this);
    this.handleDesktopPointerDown = this.handleDesktopPointerDown.bind(this);
    this.handleDesktopPointerMove = this.handleDesktopPointerMove.bind(this);
    this.handleDesktopPointerUp = this.handleDesktopPointerUp.bind(this);
    this.handleDesktopPointerCancel = this.handleDesktopPointerCancel.bind(this);
    this.handleVideoOpen = this.handleVideoOpen.bind(this);
    this.handleVideoModalClick = this.handleVideoModalClick.bind(this);
    this.handleVideoKeydown = this.handleVideoKeydown.bind(this);
  }

  connectedCallback() {
    const refs = this.collectRefs();
    if (!refs) return;

    this.refs = refs;
    this.bindButtonEvents();
    this.bindDesktopDragEvents();
    this.bindVideoEvents();
    this.syncStaticState();
    this.initSwiper();
  }

  disconnectedCallback() {
    if (this.initRetryTimer !== null) {
      window.clearTimeout(this.initRetryTimer);
      this.initRetryTimer = null;
    }
    this.destroySwiper();
    this.unbindButtonEvents();
    this.unbindDesktopDragEvents();
    this.unbindVideoEvents();
    this.closeVideo();
    this.resetDesktopDragState();
  }

  collectRefs() {
    const sliderRoot = this.querySelector('[data-slider-root]');
    const track = this.querySelector('.dl-page-review-cards-y__track');
    const slides = Array.from(this.querySelectorAll('.dl-page-review-cards-y__slide'));
    if (!sliderRoot || !track || slides.length === 0) return null;

    return {
      sliderRoot,
      track,
      slides,
      prevButton: this.querySelector('[data-action="prev"]'),
      nextButton: this.querySelector('[data-action="next"]'),
      controls: this.querySelector('[data-slider-controls]')
    };
  }

  syncStaticState() {
    if (!this.refs) return;
    const totalSlides = this.refs.slides.length;
    if (this.refs.controls) {
      this.refs.controls.hidden = totalSlides <= 1;
    }
    this.currentDesktopSnapIndex = 0;
    this.updateActiveSlide(0);
    this.updateButtonState(true, totalSlides <= 1);
    this.updateButtonState(false, totalSlides <= 1);
  }

  initSwiper() {
    if (!this.refs || this.refs.slides.length <= 1) return;

    if (typeof Swiper === 'undefined') {
      this.initRetryTimer = window.setTimeout(() => {
        this.initRetryTimer = null;
        this.initSwiper();
      }, 120);
      return;
    }

    this.destroySwiper();

    this.swiper = new Swiper(this.refs.sliderRoot, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: this.getMobileSpaceBetween(),
      slidesOffsetBefore: this.getMobileSideOffset(),
      slidesOffsetAfter: this.getMobileSideOffset(),
      allowTouchMove: true,
      simulateTouch: true,
      speed: 500,
      loop: false,
      watchOverflow: true,
      resistance: true,
      resistanceRatio: 0.85,
      observer: true,
      observeParents: true,
      breakpoints: {
        991: {
          slidesPerView: 'auto',
          slidesPerGroup: 1,
          spaceBetween: this.getDesktopSpaceBetween(),
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          allowTouchMove: false,
          simulateTouch: false
        }
      },
      on: {
        init: () => {
          this.applyResponsiveSwiperMetrics();
          this.syncDesktopLayout();
          this.syncSwiperState();
        },
        slideChange: () => {
          this.syncSwiperState();
        },
        update: () => {
          this.syncDesktopLayout();
          this.syncSwiperState();
        },
        resize: () => {
          this.applyResponsiveSwiperMetrics();
          this.syncDesktopLayout();
          this.syncSwiperState();
        }
      }
    });
  }

  isDesktop() {
    return window.innerWidth >= 991;
  }

  getDesktopSpaceBetween() {
    const viewportWidth = window.innerWidth;
    if (viewportWidth >= 1921) return (18 / 1920) * viewportWidth;
    if (viewportWidth >= 1600) return 18;
    if (viewportWidth >= 991) return (18 / 1600) * viewportWidth;
    return 0;
  }

  getMobileSpaceBetween() {
    return (12 / 375) * window.innerWidth;
  }

  getMobileSideOffset() {
    return (24 / 375) * window.innerWidth;
  }

  getDesktopLayoutMetrics() {
    if (!this.refs || !this.isDesktop()) return null;

    const { sliderRoot, controls, slides } = this.refs;
    if (!controls || slides.length === 0) return null;

    const sliderRect = sliderRoot.getBoundingClientRect();
    const controlsRect = controls.getBoundingClientRect();
    if (sliderRect.width <= 0 || controlsRect.width <= 0) return null;

    const spaceBetween = this.getDesktopSpaceBetween();
    const slideWidths = slides.map((slide) => slide.getBoundingClientRect().width);
    if (slideWidths.some((width) => width <= 0)) return null;

    const slideStartOffsets = [];
    let totalTrackWidth = 0;
    slideWidths.forEach((width, index) => {
      slideStartOffsets.push(totalTrackWidth);
      totalTrackWidth += width;
      if (index < slideWidths.length - 1) totalTrackWidth += spaceBetween;
    });

    const leadingOffset = Math.max(controlsRect.left - sliderRect.left, 0);
    const targetRight = controlsRect.right - sliderRect.left;
    const finalTranslate = targetRight - totalTrackWidth;

    return { leadingOffset, finalTranslate, slideStartOffsets };
  }

  getDesktopSnapPositions() {
    const metrics = this.getDesktopLayoutMetrics();
    if (!metrics) return [0];

    const { leadingOffset, finalTranslate, slideStartOffsets } = metrics;
    const finalPosition = Math.min(finalTranslate, leadingOffset);
    const positions = [leadingOffset];
    const threshold = 0.5;

    slideStartOffsets.slice(1).forEach((offset) => {
      const nextTranslate = leadingOffset - offset;
      if (nextTranslate > finalPosition + threshold) {
        positions.push(nextTranslate);
      }
    });

    const lastPosition = positions[positions.length - 1];
    if (Math.abs(lastPosition - finalPosition) > threshold) {
      positions.push(finalPosition);
    }

    return positions;
  }

  syncDesktopLayout() {
    if (!this.refs) return;

    if (!this.isDesktop()) {
      this.desktopSnapPositions = [];
      this.currentDesktopSnapIndex = 0;
      this.resetDesktopDragState();
      return;
    }

    const nextPositions = this.getDesktopSnapPositions();
    const nextIndex = Math.min(this.currentDesktopSnapIndex, nextPositions.length - 1);
    this.desktopSnapPositions = nextPositions;
    this.currentDesktopSnapIndex = nextIndex;
    this.applyDesktopSnapPosition(nextIndex, 0);
    this.syncDesktopDragAffordance();
  }

  applyDesktopSnapPosition(targetIndex, durationMs) {
    if (!this.refs || !this.isDesktop() || this.desktopSnapPositions.length === 0) return;

    const safeIndex = Math.min(Math.max(targetIndex, 0), this.desktopSnapPositions.length - 1);
    const translateX = this.desktopSnapPositions[safeIndex];

    this.refs.track.style.transitionProperty = 'transform';
    this.refs.track.style.transitionTimingFunction = 'ease';
    this.refs.track.style.transitionDuration = `${durationMs}ms`;
    this.refs.track.style.transform = `translate3d(${translateX}px, 0px, 0px)`;
    this.currentDesktopSnapIndex = safeIndex;
  }

  goToDesktopSnap(targetIndex) {
    if (!this.isDesktop() || this.desktopSnapPositions.length === 0) return;
    const safeIndex = Math.min(Math.max(targetIndex, 0), this.desktopSnapPositions.length - 1);
    if (safeIndex === this.currentDesktopSnapIndex) return;
    this.applyDesktopSnapPosition(safeIndex, 500);
    this.syncSwiperState();
  }

  getTrackTranslateX() {
    if (!this.refs) return 0;
    const fallback =
      this.desktopSnapPositions[this.currentDesktopSnapIndex] ?? this.desktopSnapPositions[0] ?? 0;
    const transform = window.getComputedStyle(this.refs.track).transform;
    if (!transform || transform === 'none') return fallback;

    const matrixMatch = transform.match(/matrix(3d)?\((.+)\)/);
    if (!matrixMatch) return fallback;
    const values = matrixMatch[2].split(',').map((value) => Number.parseFloat(value.trim()));
    if (matrixMatch[1] === '3d') {
      return Number.isFinite(values[12]) ? values[12] : fallback;
    }
    return Number.isFinite(values[4]) ? values[4] : fallback;
  }

  clampDesktopTranslate(translateX) {
    if (this.desktopSnapPositions.length === 0) return translateX;
    const minTranslate = Math.min(...this.desktopSnapPositions);
    const maxTranslate = Math.max(...this.desktopSnapPositions);
    return Math.min(Math.max(translateX, minTranslate), maxTranslate);
  }

  getNearestDesktopSnapIndex(translateX) {
    if (this.desktopSnapPositions.length === 0) return 0;
    let nearestIndex = 0;
    let nearestDistance = Math.abs(this.desktopSnapPositions[0] - translateX);
    this.desktopSnapPositions.forEach((position, index) => {
      const distance = Math.abs(position - translateX);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });
    return nearestIndex;
  }

  finishDesktopDrag() {
    if (!this.isDesktopDragging) return;
    const nearestIndex = this.getNearestDesktopSnapIndex(this.desktopDragCurrentTranslate);
    this.resetDesktopDragState();
    this.applyDesktopSnapPosition(nearestIndex, 300);
    this.syncSwiperState();
  }

  resetDesktopDragState() {
    if (
      this.refs &&
      this.desktopDragPointerId !== null &&
      this.refs.sliderRoot.hasPointerCapture(this.desktopDragPointerId)
    ) {
      this.refs.sliderRoot.releasePointerCapture(this.desktopDragPointerId);
    }
    this.isDesktopDragging = false;
    this.desktopDragPointerId = null;
    this.desktopDragStartX = 0;
    this.desktopDragStartTranslate = 0;
    this.desktopDragCurrentTranslate = 0;
    this.syncDesktopDragAffordance();
  }

  syncDesktopDragAffordance() {
    if (!this.refs) return;
    const shouldShowDragCursor = this.isDesktop() && this.desktopSnapPositions.length > 1;
    this.refs.sliderRoot.style.cursor = shouldShowDragCursor
      ? this.isDesktopDragging
        ? 'grabbing'
        : 'grab'
      : '';
    this.refs.sliderRoot.style.userSelect = this.isDesktopDragging ? 'none' : '';
  }

  resetDesktopTrackStyles() {
    if (!this.refs) return;
    this.refs.track.style.transitionProperty = '';
    this.refs.track.style.transitionTimingFunction = '';
    this.refs.track.style.transitionDuration = '';
    this.refs.track.style.transform = '';
  }

  applyResponsiveSwiperMetrics() {
    if (!this.swiper) return;
    if (!this.swiper.params) return;

    const isDesktop = this.isDesktop();
    if (!isDesktop && this.refs) {
      this.resetDesktopTrackStyles();
      this.desktopSnapPositions = [];
      this.currentDesktopSnapIndex = 0;
      this.resetDesktopDragState();
    }

    this.swiper.params.spaceBetween = isDesktop
      ? this.getDesktopSpaceBetween()
      : this.getMobileSpaceBetween();
    this.swiper.params.slidesOffsetBefore = isDesktop ? 0 : this.getMobileSideOffset();
    this.swiper.params.slidesOffsetAfter = isDesktop ? 0 : this.getMobileSideOffset();
    this.swiper.update?.();
  }

  syncSwiperState() {
    if (!this.refs) return;

    if (this.isDesktop()) {
      const activeIndex = this.getDesktopActiveIndex();
      const isBeginning = this.currentDesktopSnapIndex <= 0;
      const isEnd = this.currentDesktopSnapIndex >= Math.max(this.desktopSnapPositions.length - 1, 0);
      this.updateActiveSlide(activeIndex);
      this.updateButtonState(true, isBeginning);
      this.updateButtonState(false, isEnd);
      return;
    }

    const activeIndex = this.getActiveIndex();
    this.updateActiveSlide(activeIndex);
    this.updateButtonState(true, this.getIsBeginning(activeIndex));
    this.updateButtonState(false, this.getIsEnd(activeIndex));
  }

  getActiveIndex() {
    if (!this.swiper || typeof this.swiper.activeIndex !== 'number') return 0;
    const maxIndex = Math.max(this.refs ? this.refs.slides.length - 1 : 0, 0);
    return Math.min(Math.max(this.swiper.activeIndex, 0), maxIndex);
  }

  getDesktopActiveIndex() {
    if (!this.refs || this.refs.slides.length === 0) return 0;
    if (this.desktopSnapPositions.length <= 1) return 0;
    if (this.currentDesktopSnapIndex >= this.desktopSnapPositions.length - 1) {
      return this.refs.slides.length - 1;
    }
    return Math.min(this.currentDesktopSnapIndex, this.refs.slides.length - 1);
  }

  getIsBeginning(activeIndex) {
    if (this.swiper && typeof this.swiper.isBeginning === 'boolean') return this.swiper.isBeginning;
    return activeIndex <= 0;
  }

  getIsEnd(activeIndex) {
    if (this.refs && this.refs.slides.length <= 1) return true;
    if (this.swiper && typeof this.swiper.isEnd === 'boolean') return this.swiper.isEnd;
    if (!this.refs) return true;
    return activeIndex >= Math.max(this.refs.slides.length - 1, 0);
  }

  updateActiveSlide(activeIndex) {
    if (!this.refs) return;
    this.refs.slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === activeIndex);
    });
  }

  updateButtonState(isPrev, isDisabled) {
    if (!this.refs) return;
    const button = isPrev ? this.refs.prevButton : this.refs.nextButton;
    if (!button) return;
    button.disabled = isDisabled;
    button.classList.toggle('is-disabled', isDisabled);
  }

  handlePrevButtonClick(event) {
    event.preventDefault();
    if (this.isDesktop()) {
      this.goToDesktopSnap(this.currentDesktopSnapIndex - 1);
      return;
    }
    this.swiper?.slidePrev?.();
  }

  handleNextButtonClick(event) {
    event.preventDefault();
    if (this.isDesktop()) {
      this.goToDesktopSnap(this.currentDesktopSnapIndex + 1);
      return;
    }
    this.swiper?.slideNext?.();
  }

  handleDesktopPointerDown(event) {
    if (!this.refs || !this.isDesktop() || this.desktopSnapPositions.length <= 1) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('[data-review-video-open]')) return;

    this.isDesktopDragging = true;
    this.desktopDragPointerId = event.pointerId;
    this.desktopDragStartX = event.clientX;
    this.desktopDragStartTranslate = this.getTrackTranslateX();
    this.desktopDragCurrentTranslate = this.desktopDragStartTranslate;
    this.refs.sliderRoot.setPointerCapture(event.pointerId);
    this.refs.track.style.transitionDuration = '0ms';
    this.syncDesktopDragAffordance();
    event.preventDefault();
  }

  handleDesktopPointerMove(event) {
    if (
      !this.refs ||
      !this.isDesktopDragging ||
      this.desktopDragPointerId === null ||
      event.pointerId !== this.desktopDragPointerId
    ) {
      return;
    }
    const deltaX = event.clientX - this.desktopDragStartX;
    const nextTranslate = this.clampDesktopTranslate(this.desktopDragStartTranslate + deltaX);
    this.refs.track.style.transform = `translate3d(${nextTranslate}px, 0px, 0px)`;
    this.desktopDragCurrentTranslate = nextTranslate;
    event.preventDefault();
  }

  handleDesktopPointerUp(event) {
    if (
      !this.isDesktopDragging ||
      this.desktopDragPointerId === null ||
      event.pointerId !== this.desktopDragPointerId
    ) {
      return;
    }
    this.finishDesktopDrag();
  }

  handleDesktopPointerCancel(event) {
    if (
      !this.isDesktopDragging ||
      this.desktopDragPointerId === null ||
      event.pointerId !== this.desktopDragPointerId
    ) {
      return;
    }
    this.finishDesktopDrag();
  }

  bindVideoEvents() {
    this.videoModal = this.querySelector('[data-review-video-modal]');
    this.videoPlayer = this.querySelector('[data-review-video-player]');
    this.querySelectorAll('[data-review-video-open]').forEach((button) => {
      button.addEventListener('click', this.handleVideoOpen);
      button.addEventListener('pointerdown', this.stopVideoPointer);
    });
    this.videoModal?.addEventListener('click', this.handleVideoModalClick);
    document.addEventListener('keydown', this.handleVideoKeydown);
  }

  unbindVideoEvents() {
    this.querySelectorAll('[data-review-video-open]').forEach((button) => {
      button.removeEventListener('click', this.handleVideoOpen);
      button.removeEventListener('pointerdown', this.stopVideoPointer);
    });
    this.videoModal?.removeEventListener('click', this.handleVideoModalClick);
    document.removeEventListener('keydown', this.handleVideoKeydown);
  }

  stopVideoPointer(event) {
    event.stopPropagation();
  }

  handleVideoOpen(event) {
    event.preventDefault();
    event.stopPropagation();
    const slide = event.currentTarget.closest('.dl-page-review-cards-y__slide');
    const template = slide?.querySelector('[data-review-video-template]');
    if (!template || !this.videoModal || !this.videoPlayer) return;

    this.videoPlayer.replaceChildren(template.content.cloneNode(true));
    this.videoModal.hidden = false;
    document.documentElement.classList.add('dl-review-cards-y-modal-open');
    document.body.classList.add('dl-review-cards-y-modal-open');
    const video = this.videoPlayer.querySelector('video');
    if (video) {
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.play?.().catch(() => {});
    }
    this.videoModal.querySelector('[data-review-video-close]')?.focus();
  }

  handleVideoModalClick(event) {
    if (event.target === this.videoModal || event.target.closest('[data-review-video-close]')) {
      this.closeVideo();
    }
  }

  handleVideoKeydown(event) {
    if (event.key !== 'Escape' || !this.videoModal || this.videoModal.hidden) return;
    event.preventDefault();
    this.closeVideo();
  }

  closeVideo() {
    if (!this.videoModal || !this.videoPlayer) return;
    const video = this.videoPlayer.querySelector('video');
    video?.pause?.();
    this.videoPlayer.replaceChildren();
    this.videoModal.hidden = true;
    document.documentElement.classList.remove('dl-review-cards-y-modal-open');
    document.body.classList.remove('dl-review-cards-y-modal-open');
  }

  bindButtonEvents() {
    if (!this.refs) return;
    this.refs.prevButton?.removeEventListener('click', this.handlePrevButtonClick);
    this.refs.nextButton?.removeEventListener('click', this.handleNextButtonClick);
    this.refs.prevButton?.addEventListener('click', this.handlePrevButtonClick);
    this.refs.nextButton?.addEventListener('click', this.handleNextButtonClick);
  }

  unbindButtonEvents() {
    if (!this.refs) return;
    this.refs.prevButton?.removeEventListener('click', this.handlePrevButtonClick);
    this.refs.nextButton?.removeEventListener('click', this.handleNextButtonClick);
  }

  bindDesktopDragEvents() {
    if (!this.refs) return;
    this.refs.sliderRoot.removeEventListener('pointerdown', this.handleDesktopPointerDown);
    this.refs.sliderRoot.removeEventListener('pointermove', this.handleDesktopPointerMove);
    this.refs.sliderRoot.removeEventListener('pointerup', this.handleDesktopPointerUp);
    this.refs.sliderRoot.removeEventListener('pointercancel', this.handleDesktopPointerCancel);
    this.refs.sliderRoot.addEventListener('pointerdown', this.handleDesktopPointerDown);
    this.refs.sliderRoot.addEventListener('pointermove', this.handleDesktopPointerMove);
    this.refs.sliderRoot.addEventListener('pointerup', this.handleDesktopPointerUp);
    this.refs.sliderRoot.addEventListener('pointercancel', this.handleDesktopPointerCancel);
  }

  unbindDesktopDragEvents() {
    if (!this.refs) return;
    this.refs.sliderRoot.removeEventListener('pointerdown', this.handleDesktopPointerDown);
    this.refs.sliderRoot.removeEventListener('pointermove', this.handleDesktopPointerMove);
    this.refs.sliderRoot.removeEventListener('pointerup', this.handleDesktopPointerUp);
    this.refs.sliderRoot.removeEventListener('pointercancel', this.handleDesktopPointerCancel);
  }

  destroySwiper() {
    this.resetDesktopTrackStyles();
    this.desktopSnapPositions = [];
    this.currentDesktopSnapIndex = 0;
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
  }
}

if (!customElements.get('dl-page-review-cards-y')) {
  customElements.define('dl-page-review-cards-y', DlPageReviewCardsY);
}
